import { createEntityAdapter } from "@reduxjs/toolkit";
import { collection, onSnapshot } from "firebase/firestore";
import type { Firestore } from "firebase/firestore";

import { ufflRoundConverter } from "../models/firestoreDataConverters";
import { UfflRound } from "../models/round";
import { InferSelectFromResult, selectByIdFactory } from "../redux/selectorFactories";

import { firestoreApi, firestoreBaseCollection } from "./firestoreApi";

const ufflRoundsAdapter = createEntityAdapter({
  selectId: (e: UfflRound) => e.providerId
});

const initialState = ufflRoundsAdapter.getInitialState();

export const ufflRoundsApi = firestoreApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (build) => ({
    getUfflRounds: build.query<
      ReturnType<typeof ufflRoundsAdapter["getInitialState"]>,
      { firestore: Firestore; compSeasonId: string } // TODO: temporary, prefer for this to be in the base query or just set in a single place
    >({
      queryFn: () => {
        return { data: initialState };
      },
      async onCacheEntryAdded({ firestore, compSeasonId }, { cacheEntryRemoved, getState, updateCachedData }) {
        let unsubscribe;
        try {
          const ref = collection(
            firestoreBaseCollection(firestore, getState),
            "uffl",
            "compSeasons",
            compSeasonId,
            "rounds"
          ).withConverter(ufflRoundConverter);
          unsubscribe = onSnapshot(ref, {
            next: (snapshot) => {
              updateCachedData((draft) => {
                const docs = snapshot?.docs.map((s) => s.data()) ?? [];
                // console.log("getUfflRounds updateCachedData", { draft, snapshot, docs: docs });
                ufflRoundsAdapter.setAll(draft, docs);
              });
            },
            error: (error) => {
              console.log("Firestore error: getUfflRounds", { error });
              // TODO: update cached data with error
            }
          });
        } catch (error) {
          console.log("Uncaught error: getUfflRounds", { error });
          throw new Error("Something went wrong with getUfflRounds.");
          // TODO: update cached data with error?
        }

        await cacheEntryRemoved;
        return unsubscribe();
      }
    })
  })
});

export const { useGetUfflRoundsQuery } = ufflRoundsApi;

const { selectById: selectByKnownId, ...selectors } = ufflRoundsAdapter.getSelectors(
  (state: InferSelectFromResult<typeof useGetUfflRoundsQuery>) => state?.data ?? initialState
);

export const ufflRoundsSelectors = {
  ...selectors,
  selectById: selectByIdFactory(selectByKnownId)
};
